export const DEFAULT_SEARCH_BY =
  "firstName,lastName,email,subscription.plan,account.firstName,account.lastName,account.email";

const DEFAULT_PLAN_COLOR = "#64748B";

export const TABLE_COLUMNS = [
  "User",
  "Plan",
  "Status",
  "Joined",
  "User Type",
  "Actions",
];

export const PAPER_STYLES = {
  borderRadius: "16px",
  border: "1px solid #E2E8F0",
  overflow: "hidden",
};

export const HEADER_CELL_STYLES = {
  fontWeight: 600,
  color: "#475569",
};

const ROW_ACCENTS = [
  { rowBg: "#EFF6FF", bar: "#2563EB" },
  { rowBg: "#FFF7ED", bar: "#EA580C" },
  { rowBg: "#F5F3FF", bar: "#7C3AED" },
  { rowBg: "#FDF2F8", bar: "#DB2777" },
  { rowBg: "#ECFEFF", bar: "#0891B2" },
  { rowBg: "#FFF1F2", bar: "#E11D48" },
];

const PLAN_STYLES = {
  premium: { bg: "#7C3AED", color: "#FFFFFF", tint: "#F3E8FF" },
  pro: { bg: "#2563EB", color: "#FFFFFF", tint: "#DBEAFE" },
  basic: { bg: "#0F766E", color: "#FFFFFF", tint: "#CCFBF1" },
  starter: { bg: "#EA580C", color: "#FFFFFF", tint: "#FFEDD5" },
  trial: { bg: "#0891B2", color: "#FFFFFF", tint: "#CFFAFE" },
  free: { bg: "#D97706", color: "#FFFFFF", tint: "#FEF3C7" },
  enterprise: { bg: "#BE123C", color: "#FFFFFF", tint: "#FFE4E6" },
  business: { bg: "#4338CA", color: "#FFFFFF", tint: "#E0E7FF" },
};

const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return { bg: "#DCFCE7", color: "#16A34A" };
    case "Trial":
      return { bg: "#DBEAFE", color: "#1F4FFF" };
    case "Grace Period":
      return { bg: "#FEF3C7", color: "#F59E0B" };
    case "Inactive":
      return { bg: "#FEE2E2", color: "#DC2626" };
    default:
      return { bg: "#F1F5F9", color: "#64748B" };
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 10);
};

const getAccentIndex = (value = "") =>
  Array.from(String(value)).reduce(
    (total, char) => total + char.charCodeAt(0),
    0,
  );

const getNormalizedPlanKey = (planName) => {
  const normalizedPlan = String(planName || "")
    .trim()
    .toLowerCase();

  if (!normalizedPlan || normalizedPlan === "unknown") return "free";
  if (normalizedPlan.includes("enterprise")) return "enterprise";
  if (normalizedPlan.includes("premium")) return "premium";
  if (normalizedPlan.includes("business")) return "business";
  if (normalizedPlan.includes("starter")) return "starter";
  if (normalizedPlan.includes("basic")) return "basic";
  if (normalizedPlan.includes("trial")) return "trial";
  if (normalizedPlan.includes("free")) return "free";
  if (normalizedPlan.includes("pro")) return "pro";

  return normalizedPlan;
};

const getPlanStyle = (planName, apiColor) => {
  const normalizedPlan = getNormalizedPlanKey(planName);

  if (PLAN_STYLES[normalizedPlan]) {
    return PLAN_STYLES[normalizedPlan];
  }

  if (apiColor && apiColor !== DEFAULT_PLAN_COLOR) {
    return {
      bg: apiColor,
      color: "#FFFFFF",
      tint: `${apiColor}1A`,
    };
  }

  const accent =
    ROW_ACCENTS[getAccentIndex(normalizedPlan) % ROW_ACCENTS.length];

  return {
    bg: accent.bar,
    color: "#FFFFFF",
    tint: accent.rowBg,
  };
};

const getRowAccent = (user) => {
  const key =
    user?._id ??
    user?.id ??
    user?.account?._id ??
    user?.account?.id ??
    user?.email ??
    user?.account?.email ??
    "";

  return ROW_ACCENTS[getAccentIndex(key) % ROW_ACCENTS.length];
};

export const getAccountId = (user) =>
  user?.account?._id ??
  user?.account?.id ??
  user?.accountId ??
  user?.user?._id ??
  user?._id ??
  user?.id;

export const buildUserRow = (user) => {
  const account = user.account || user.user || {};
  const firstName = user.firstName ?? account.firstName ?? account.first_name;
  const lastName = user.lastName ?? account.lastName ?? account.last_name;
  const name =
    [firstName, lastName].filter(Boolean).join(" ") ||
    user.name ||
    account.name ||
    "Unknown";
  const email = user.email ?? account.email ?? "-";
  const planName =
    user.subscription?.plan?.name ??
    user.plan?.name ??
    user.subscription?.plan ??
    user.plan ??
    "Unknown";
  const planColor =
    user.subscription?.plan?.color ?? user.plan?.color ?? DEFAULT_PLAN_COLOR;
  const status = account.accountStatus;

  return {
    id: user._id ?? user.id ?? email,
    accountId: getAccountId(user),
    name,
    email,
    joinedDate: formatDate(user.createdAt ?? account.createdAt ?? user.joined),
    userType: account.userType ?? "-",
    status: status || "-",
    statusStyle: getStatusColor(status),
    planName,
    planStyle: getPlanStyle(planName, planColor),
    rowAccent: getRowAccent(user),
  };
};
